import { useEffect, useRef } from "react";

const MAX_TRANSLATE_PX = 100;
const MIN_SCALE = 0.95;

/**
 * design.xml hero parallax: over the first half-viewport of scroll the hero fades 1 → 0,
 * scales 1 → 0.95 and moves down 100px. Written straight to the element (no re-render per
 * frame) and skipped entirely when the user prefers reduced motion.
 */
export function useHeroParallax<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const progress = Math.min(1, window.scrollY / (window.innerHeight * 0.5));
      element.style.opacity = String(1 - progress);
      element.style.transform = `translateY(${progress * MAX_TRANSLATE_PX}px) scale(${1 - progress * (1 - MIN_SCALE)})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}
